import axios from 'axios';
import {
  Publisher,
  Right,
  Sequence,
  Enrollment,
  AuthResponse,
  PaginatedResponse,
  ImportStats,
  User,
  CatalogStats,
  TrackRecord
} from '../types/crm';
import { RawTrack } from '../types/music';

class CRMService {
  private BASE_URL = import.meta.env.VITE_CRM_API_URL ?? (
    import.meta.env.MODE === 'production' || import.meta.env.PROD
      ? 'https://blagmanbros.com/api/crm'
      : 'http://127.0.0.1:8001/api/crm'
  );
  private token: string | null = null;
  private headers: Record<string, string> = {};

  constructor() {
    this.token = localStorage.getItem('crm_token');
    if (this.token) {
      this.headers = { 'Authorization': `Bearer ${this.token}` };
    }
  }

  private withNoCache(headers: Record<string, string> = {}) {
    return {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...this.headers,
      ...headers
    };
  }

  // Auth Methods
  async login(credentials: { username: string; password?: string }): Promise<AuthResponse> {
    const response = await axios.post(`${this.BASE_URL}/auth/login`, credentials);
    this.token = response.data.token;
    if (this.token) {
      localStorage.setItem('crm_token', this.token);
      this.headers = { 'Authorization': `Bearer ${this.token}` };
    }
    return response.data;
  }

  logout() {
    this.token = null;
    localStorage.removeItem('crm_token');
    this.headers = {};
  }

  async changePassword(current_password: string, new_password: string): Promise<{ message: string }> {
    const response = await axios.patch(`${this.BASE_URL}/auth/change-password`, {
      current_password,
      new_password
    }, { headers: this.withNoCache() });
    return response.data;
  }

  // Publisher Methods
  async listPublishers(params: { q?: string; stage?: string; tag?: string } = {}): Promise<PaginatedResponse<Publisher>> {
    const response = await axios.get(`${this.BASE_URL}/publishers`, { params, headers: this.withNoCache() });
    return response.data;
  }

  async createPublisher(data: Partial<Publisher>): Promise<Publisher> {
    const response = await axios.post(`${this.BASE_URL}/publishers`, data, { headers: this.withNoCache() });
    return response.data;
  }

  async getPublisher(id: number): Promise<Publisher> {
    const response = await axios.get(`${this.BASE_URL}/publishers/${id}`, { headers: this.withNoCache() });
    return response.data;
  }

  async updatePublisher(id: number, data: Partial<Publisher>): Promise<Publisher> {
    const response = await axios.put(`${this.BASE_URL}/publishers/${id}`, data, { headers: this.withNoCache() });
    return response.data;
  }

  // Rights Management
  async addRight(publisherId: number, data: Partial<Right>): Promise<Right> {
    const response = await axios.post(`${this.BASE_URL}/publishers/${publisherId}/rights`, data, { headers: this.withNoCache() });
    return response.data;
  }

  async listRights(publisherId: number): Promise<Right[]> {
    const response = await axios.get(`${this.BASE_URL}/publishers/${publisherId}/rights`, { headers: this.withNoCache() });
    return response.data;
  }

  // Email Sequences
  async listSequences(): Promise<Sequence[]> {
    const response = await axios.get(`${this.BASE_URL}/sequences`, { headers: this.withNoCache() });
    return response.data;
  }

  async enrollPublisher(publisherId: number, sequenceId: number): Promise<Enrollment> {
    const response = await axios.post(`${this.BASE_URL}/sequences/${sequenceId}/enroll`, {
      publisher_id: publisherId
    }, { headers: this.withNoCache() });
    return response.data;
  }

  // Import/Export
  async importPublishers(file: File): Promise<ImportStats> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${this.BASE_URL}/publishers/import`, formData, {
      headers: this.withNoCache({ 'Content-Type': 'multipart/form-data' })
    });
    return response.data;
  }

  // Dashboard Methods
  async getCatalogOverview(): Promise<CatalogStats> {
    const response = await axios.get(`${this.BASE_URL}/dashboard/overview`, { headers: this.withNoCache() });
    return response.data;
  }

  async assignPublisherToTrack(trackId: string, publisherId: number, data: { rightsType: string; notes?: string }): Promise<void> {
    await axios.post(`${this.BASE_URL}/tracks/${trackId}/publisher`, { publisherId, ...data }, { headers: this.withNoCache() });
  }

  async getCurrentUser(): Promise<{ user: User }> {
    const response = await axios.get(`${this.BASE_URL}/auth/me`, { headers: this.withNoCache() });
    return response.data;
  }

  // Import Methods
  async uploadMusicCatalog(file: File): Promise<ImportStats> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${this.BASE_URL}/import/catalog`, formData, {
      headers: this.withNoCache({ 'Content-Type': 'multipart/form-data' })
    });
    return response.data;
  }

  async uploadCsv(file: File): Promise<ImportStats> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${this.BASE_URL}/import/csv`, formData, {
      headers: this.withNoCache({ 'Content-Type': 'multipart/form-data' })
    });
    return response.data;
  }

  async runSchedulerOnce(): Promise<{ message: string }> {
    const response = await axios.post(`${this.BASE_URL}/scheduler/run`, {}, { headers: this.withNoCache() });
    return response.data;
  }

  async updatePublisherSongs(id: number, songs: RawTrack[]): Promise<Publisher> {
    const response = await axios.put(`${this.BASE_URL}/publishers/${id}/songs`, { songs }, { headers: this.withNoCache() });
    return response.data;
  }

  async listPublishersWithSongs(): Promise<{ publishers: Publisher[] }> {
    const response = await axios.get(`${this.BASE_URL}/publishers/with-songs`, { headers: this.withNoCache() });
    return response.data;
  }

  async createSequence(data: { name: string; description: string; steps: { step_number: number; channel: string; subject: string; body_template: string; delay_hours: number }[] }): Promise<Sequence> {
    const response = await axios.post(`${this.BASE_URL}/sequences`, data, { headers: this.withNoCache() });
    return response.data;
  }

  async listSongs(): Promise<{ songs: RawTrack[] }> {
    const response = await axios.get(`${this.BASE_URL}/songs`, { headers: this.withNoCache() });
    return response.data;
  }

  // Track Methods
  async listTracks(params: { q?: string; media_type?: string; rights_owned?: string; page?: number; limit?: number } = {}): Promise<PaginatedResponse<TrackRecord>> {
    const url = new URL(`${this.BASE_URL}/tracks`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
    url.searchParams.set('_t', Date.now().toString()); // cache buster

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.withNoCache(),
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`Failed to load tracks: ${response.status}`);
    }

    return await response.json();
  }

  async updateTrack(id: string, data: Partial<TrackRecord>): Promise<TrackRecord> {
    const url = `${this.BASE_URL}/tracks/${id}?_t=${Date.now()}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...this.withNoCache({ 'Content-Type': 'application/json' })
      },
      cache: 'no-store',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }
}
export const crmService = new CRMService();
